<template>
  <el-card v-if="value[name]" style="margin-bottom: 20px">
    <div slot="header">
      <h3>
        {{ setting.label || name }}
        <Tooltip :label="setting.label || name" :content="setting.detail" />
      </h3>
    </div>
    <div style="display: flex; flex-direction: column">
      <component
        v-model="value[name]"
        v-for="(childSetting, childKey) in setting.children"
        :key="childKey"
        :name="childKey"
        :setting="childSetting"
        :is="toComponent(childSetting)"
        :modules="modules"
      ></component>
    </div>
  </el-card>
</template>

<script>
/**
 * SmartFieldObject - Nested form for complex object configuration values.
 *
 * @component
 * @description Displays a card containing child SmartFields based on the setting's children configuration.
 * This component is recursive and can contain any other SmartField type, allowing for deeply nested
 * configuration objects. Each child is rendered as its appropriate SmartField type dynamically determined
 * by the child's type property.
 *
 * @prop {Object} value - Parent object containing the setting value
 * @prop {Object} setting - Field configuration object
 * @prop {string} [setting.label] - Display label for the object card (defaults to name)
 * @prop {string} [setting.detail] - Tooltip text explaining the setting
 * @prop {Object} setting.children - Object mapping child keys to their SmartField configurations
 * @prop {Object} [setting.default] - Default object structure if not set
 * @prop {string} name - Property key in the value object
 * @prop {Object} [modules] - Module definitions passed to child components
 *
 * @example
 * <SmartFieldObject
 *   :value="config"
 *   :setting="{
 *     label: 'API Settings',
 *     detail: 'Configure API connection',
 *     children: {
 *       url: { type: 'string', label: 'API URL' },
 *       timeout: { type: 'number', label: 'Timeout', min: 0 },
 *       enabled: { type: 'boolean', label: 'Enable API' }
 *     },
 *     default: { url: '', timeout: 30, enabled: true }
 *   }"
 *   name="apiSettings"
 * />
 */
import _ from "lodash";
import SmartFieldString from "./String";
import SmartFieldList from "./List";
import SmartFieldNumber from "./Number";
import SmartFieldAutocomplete from "./Autocomplete";
import SmartFieldTimerange from "./Timerange";
import SmartFieldDaterange from "./Daterange";
import SmartFieldDate from "./Date";
import SmartFieldTag from "./Tag";
import SmartFieldBoolean from "./Boolean";
import SmartFieldColor from "./Color";
import SmartFieldSelect from "./Select";
import SmartFieldMultiselect from "./Multiselect";
import SmartFieldTextarea from "./Textarea";
import SmartFieldJson from "./Json";
import SmartFieldButton from "./Button";
import SmartFieldTransfer from "./Transfer";
import SmartFieldCascader from "./Cascader";
import SmartFieldCascaderwithheaders from "./CascaderWithHeaders";
import SmartFieldDatetime from "./DateTime";
// import SmartFieldHandoverroutingskillslibrary from "./Custom/HandoverRoutingSkillsLibrary";
import SmartFieldOfficehourdepartmentdatetimes from "./Custom/OfficeHourDepartmentDatetimes";
import SmartFieldOfficehourdatetimes from "./Custom/OfficeHourDatetimes";
import SmartFieldStickymenulanguage from "./Custom/StickyMenuLanguage";
import SmartFieldAllowuploadwheninlivechat from "./Custom/AllowUploadWhenInLiveChat";
import SmartFieldSingpasscertificate from "./Custom/SingpassCertificate";
import SmartFieldTrendingtopiclanguage from "./Custom/TrendingTopicLanguage";
import SmartFieldDevcenterapikey from "./Custom/DevCenterApiKey";
import SmartFieldGenaitagcreate from "./Custom/GenAITagCreate";
import SmartFieldTimeselect from "./TimeSelect";
import SmartFieldLivechattool from "./Custom/LivechatTool.vue";
import SmartFieldDepartmentfiltergroups from "./Custom/DepartmentFilterGroups.vue";

import Tooltip from "../Tooltip.vue";
export default {
  props: ["value", "setting", "name", "modules"],
  components: {
    Tooltip,
    SmartFieldString,
    SmartFieldList,
    SmartFieldNumber,
    SmartFieldAutocomplete,
    SmartFieldTimerange,
    SmartFieldDaterange,
    SmartFieldDate,
    SmartFieldTag,
    SmartFieldBoolean,
    SmartFieldColor,
    SmartFieldSelect,
    SmartFieldMultiselect,
    SmartFieldTextarea,
    SmartFieldJson,
    SmartFieldButton,
    SmartFieldTransfer,
    SmartFieldCascader,
    SmartFieldCascaderwithheaders,
    SmartFieldDatetime,
    // SmartFieldHandoverroutingskillslibrary,
    SmartFieldOfficehourdepartmentdatetimes,
    SmartFieldOfficehourdatetimes,
    SmartFieldObject: () => import("@/components/ModuleEditor/SmartFields/Object"),
    SmartFieldStickymenulanguage,
    SmartFieldAllowuploadwheninlivechat,
    SmartFieldSingpasscertificate,
    SmartFieldTrendingtopiclanguage,
    SmartFieldDevcenterapikey,
    SmartFieldGenaitagcreate,
    SmartFieldTimeselect,
    SmartFieldLivechattool,
    SmartFieldDepartmentfiltergroups,
  },
  mounted() {
    const isNotObject = !_.isObject(this.value[this.name]);
    const isEmpty = _.isEmpty(this.value[this.name]);
    if ((isNotObject || isEmpty) && typeof this.value === "object") {
      this.$set(this.value, this.name, this.setting.default);
    }
  },
  methods: {
    toComponent(setting) {
      const type = _.capitalize(setting.type);
      const componentName = `SmartField${type}`;
      const isComponentExist = _.has(this.$options.components, componentName);
      return isComponentExist ? componentName : null;
    },
  },
};
</script>
